/**
 * Transport seam — one narrow interface between the app and the network.
 *
 * The typed mock (auth/mock/mockIdentity.ts) and the real `fetch` transport
 * implement the same shape, so flipping VITE_API_BASE_URL is the ONLY change
 * needed to go live against the backend (§16.2 contract-first rule).
 */

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface TransportRequest {
  readonly method: HttpMethod;
  /** Contract path, e.g. "/auth/login" (servers[0].url = /api/v1 is the base). */
  readonly path: string;
  readonly headers: Readonly<Record<string, string>>;
  /** JSON-serializable object, or a `FormData` for multipart uploads (M7-106). */
  readonly body?: unknown;
}

export interface TransportResponse {
  readonly status: number;
  /** Parsed JSON body; null for 204/304. */
  readonly body: unknown;
  /**
   * Response headers the client actually consumes (lower-cased keys).
   * Today that is only `retry-after` (identity.yaml 429 lockout/cooldown);
   * transports may omit the map entirely.
   */
  readonly headers?: Readonly<Record<string, string>>;
}

export type Transport = (request: TransportRequest) => Promise<TransportResponse>;

export function apiBaseUrl(): string | null {
  const value = import.meta.env.VITE_API_BASE_URL;
  return typeof value === "string" && value.length > 0 ? value : null;
}

export function isMockMode(): boolean {
  return apiBaseUrl() === null;
}

/** Real transport — plain fetch against VITE_API_BASE_URL. */
export function createFetchTransport(baseUrl: string): Transport {
  return async ({ method, path, headers, body }) => {
    const requestHeaders: Record<string, string> = { Accept: "application/json", ...headers };
    const init: RequestInit = {
      method,
      headers: requestHeaders,
      // Production stance (§11.1): the admin refresh token travels as an
      // httpOnly cookie scoped to /api/v1/auth — include credentials so the
      // gateway can read/rotate it. Harmless for all other endpoints.
      credentials: "include",
    };
    if (body instanceof FormData) {
      // Multipart (M7-106 banner image upload): the body goes through as-is
      // and NO Content-Type is set — fetch writes the boundary itself, and a
      // hand-written "multipart/form-data" header would omit it.
      init.body = body;
    } else if (body !== undefined) {
      requestHeaders["Content-Type"] = "application/json";
      init.body = JSON.stringify(body);
    }
    const response = await fetch(`${baseUrl}${path}`, init);
    const text = await response.text();
    // Only the headers the app consumes cross the seam (keeps mocks honest).
    const retryAfter = response.headers.get("Retry-After");
    return {
      status: response.status,
      body: text.length > 0 ? (JSON.parse(text) as unknown) : null,
      ...(retryAfter !== null ? { headers: { "retry-after": retryAfter } } : {}),
    };
  };
}
